import { useFormContext } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import { Skeleton } from '@/components/ui/Skeleton.tsx';
import { ShieldCheck } from 'lucide-react';
import CheckboxInput from '@/components/form/CheckboxInput.tsx';
import MultiSelectInput from '@/components/form/MultiSelectInput.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import Option from '@/components/form/types/Option.ts';
import { TourInsuranceFormData } from '@/features/dashboard/tourInsurances/schemas/tourInsuranceSchema.ts';
import useGetInsurances from '@/features/dashboard/insurances/hooks/useGetInsurances.ts';
import { InsurancesTab } from '@/features/dashboard/insurances/routes/insurancesRoute.ts';

const TourInsuranceConfigCard = () => {
  const navigate = useNavigate();
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<TourInsuranceFormData>();

  const { data: insurances, isLoading } = useGetInsurances();

  const insuranceOptions: Option[] = (insurances ?? []).map((insurance) => ({
    value: insurance.id ?? '',
    label: insurance.name,
  }));

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>Ubezpieczenie uczestników</CardTitle>
        <CardDescription>
          Wybierz, które ubezpieczenia będą dostępne dla uczestników tego
          wyjazdu podczas rejestracji.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {isLoading ? (
          <div className="space-y-1.5">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : insuranceOptions.length === 0 ? (
          <NoItems
            icon={<ShieldCheck />}
            title="Brak ubezpieczeń"
            description="Aby przypisać ubezpieczenie do wyjazdu, najpierw dodaj je w module Ubezpieczenia."
          >
            <ActionButton
              tone="outline"
              size="sm"
              type="button"
              onClick={() =>
                navigate({
                  to: '/app/insurances',
                  search: { tab: InsurancesTab.TYPES },
                })
              }
            >
              Przejdź do ubezpieczeń
            </ActionButton>
          </NoItems>
        ) : (
          <>
            <MultiSelectInput
              fieldPath="insuranceIds"
              control={control}
              options={insuranceOptions}
              label="Dostępne ubezpieczenia"
              placeholder="Wybierz ubezpieczenia..."
              error={errors.insuranceIds?.message}
            />
            <CheckboxInput
              label="Wybór ubezpieczenia jest wymagany przy zapisie"
              register={register('required')}
              hint="Gdy zaznaczone, uczestnik musi wybrać jedno z przypisanych ubezpieczeń."
              error={errors.required?.message}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TourInsuranceConfigCard;
